import React, { useEffect, useState, useContext, useRef } from "react";
import { IoIosArrowDown, IoIosSearch } from "react-icons/io";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import moment from "moment";
import { Language } from "../../App";
import SearchFilter from "../../components/search/SearchFilter";
import ModernDatePicker from "../../components/common/ModernDatePicker";
import ModernMonthPicker from "../../components/common/ModernMonthPicker";
import { LuSearch, LuFilter, LuX } from "react-icons/lu";

export function Filter({
  title,
  options,
  onCountrySelect,
  onSelectCost,
  onCitySelect,
  onMonthSelect,
  onSpecializationSelect,
  onSelectDate,
  onSortBy,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useContext(Language);
  const headerRef = useRef(null);
  const selectMenuPortalTarget =
    typeof document !== "undefined" ? document.body : null;

  useEffect(() => {
    const updateStickyOffset = () => {
      if (headerRef.current) {
        const headerHeight = headerRef.current.offsetHeight;
        // 66px (NavBar) + headerHeight + 12px gap
        document.documentElement.style.setProperty(
          "--search-sticky-top",
          `${66 + headerHeight + 12}px`
        );
      }
    };

    updateStickyOffset();

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined" && headerRef.current) {
      resizeObserver = new ResizeObserver(updateStickyOffset);
      resizeObserver.observe(headerRef.current);
    }

    window.addEventListener("resize", updateStickyOffset);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", updateStickyOffset);
      document.documentElement.style.removeProperty("--search-sticky-top");
    };
  }, [options]);

  const queryParams = new URLSearchParams(location.search);
  const rawKeyword = queryParams.get("keyword") || "";
  const keyword = decodeURIComponent(rawKeyword).trim();

  const pathname = location.pathname;

  const resolvedActiveType =
    pathname.includes("/search/doctors")
      ? "doctors"
      : pathname.includes("/search/posts")
      ? "posts"
      : pathname.includes("/search/webinars")
      ? "webinars"
      : pathname.includes("/search/clinicals")
      ? "clinicals"
      : pathname.includes("/search/courses")
      ? "courses"
      : pathname.includes("/search/events")
      ? "events"
      : "all";

  function getFormattedMonth(dateString) {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date)) return null;
    const month = date.getMonth() + 1;
    return month < 10 ? `0${month}` : `${month}`;
  }

  function getFormattedYear(dateString) {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date)) return null;
    return String(date.getFullYear());
  }

  function formatDate(isoDate) {
    if (!isoDate) return null;
    const date = new Date(isoDate);
    if (isNaN(date)) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const handleSelection = (dropdownTitle, selectedItem) => {
    if (dropdownTitle === lang?.country || dropdownTitle === "Country") {
      onCountrySelect && onCountrySelect(selectedItem || []);
    } else if (dropdownTitle === "City" || dropdownTitle === lang?.city) {
      onCitySelect && onCitySelect(selectedItem ? (selectedItem?.id || selectedItem) : null);
    } else if (dropdownTitle === lang?.specialization || dropdownTitle === "Specialization") {
      onSpecializationSelect && onSpecializationSelect(selectedItem || []);
    } else if (dropdownTitle === lang?.date || dropdownTitle === "Date") {
      const formattedDate = selectedItem ? formatDate(selectedItem) : null;
      onSelectDate && onSelectDate(formattedDate);
    } else if (dropdownTitle === lang?.month || dropdownTitle === "Month") {
      if (selectedItem) {
        const month = getFormattedMonth(selectedItem);
        const year = getFormattedYear(selectedItem);
        if (month && year) {
          onMonthSelect && onMonthSelect({ month, year });
        }
      } else {
        onMonthSelect && onMonthSelect({ month: null, year: null });
      }
    } else if (dropdownTitle === "Cost" || dropdownTitle === lang?.cost) {
      onSelectCost && onSelectCost(selectedItem || null);
    } else if (dropdownTitle === "Sort By" || dropdownTitle === lang?.sort_by) {
      onSortBy && onSortBy(selectedItem || null);
    }
  };

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minWidth: "155px",
      minHeight: "34px",
      height: "34px",
      borderRadius: "17px",
      borderColor: state.isFocused ? "#00BADA" : "#E2E8F0",
      boxShadow: state.isFocused ? "0 0 0 1px rgba(0, 186, 218, 0.35)" : "none",
      fontWeight: "500",
      fontSize: "13px",
      backgroundColor: "#FFFFFF",
      fontFamily: "'Onest', sans-serif",
      padding: "0 4px",
      cursor: "pointer",
      transition: "all 0.15s ease",
      "&:hover": {
        borderColor: "#CBD5E1",
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "32px",
      padding: "0 6px",
      display: "flex",
      alignItems: "center",
      flexWrap: "nowrap",
      overflowX: "auto",
      scrollbarWidth: "none",
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
      fontSize: "13px",
      color: "#0E1421",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "32px",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "4px",
      color: "#94A3B8",
      "&:hover": {
        color: "#475569",
      },
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "2px 4px",
      color: "#0098B3",
      cursor: "pointer",
      transition: "all 0.15s ease",
      "&:hover": {
        color: "#E11D48",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#475569",
      fontSize: "13px",
      fontWeight: "500",
    }),
    menu: (provided) => ({
      ...provided,
      width: "290px",
      borderRadius: "14px",
      boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
      border: "1px solid #E2E8F0",
      zIndex: 1050,
      marginTop: "6px",
      overflow: "hidden",
      backgroundColor: "#FFFFFF",
      padding: "4px",
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 1100,
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: "260px",
      padding: "2px",
      scrollbarWidth: "thin",
    }),
    option: (provided, state) => ({
      ...provided,
      color: state.isSelected ? "#0098B3" : "#0E1421",
      fontWeight: state.isSelected ? "600" : "500",
      padding: "8px 12px",
      borderRadius: "8px",
      fontSize: "13px",
      backgroundColor: state.isSelected
        ? "#E8FBFF"
        : state.isFocused
        ? "#F8FAFC"
        : "transparent",
      cursor: "pointer",
      transition: "all 0.12s ease",
      margin: "1px 0",
      "&:hover": {
        backgroundColor: "#F8FAFC",
        color: "#0E1421",
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      display: "inline-flex",
      whiteSpace: "nowrap",
      borderRadius: "10px",
      backgroundColor: "#E8FBFF",
      color: "#0098B3",
      fontSize: "11.5px",
      margin: "1px 2px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#0098B3",
      fontWeight: "600",
      padding: "1px 5px",
      fontSize: "11.5px",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#0098B3",
      padding: "1px 3px",
      borderRadius: "0 8px 8px 0",
      "&:hover": {
        backgroundColor: "#D0F5FC",
        color: "#008199",
      },
    }),
  };

  return (
    <div ref={headerRef} className="search-hero-header">
      <div className="search-hero-inner">
        <div className="search-hero-center-col">
          {/* Top All Categories Navigation Tabs */}
          <SearchFilter keyword={keyword} activeType={resolvedActiveType} />

          {/* Filter Dropdowns Row */}
          {options && options.length > 0 && (
            <div className="search-filter-dropdowns-row">
              {options.map((option, i) => {
                if (
                  option.title === lang?.specialization ||
                  option.title === "Specialization" ||
                  option.title === lang?.country ||
                  option.title === "Country"
                ) {
                  return (
                    <div key={i} style={{ minWidth: "170px", maxWidth: "320px" }}>
                      <Select
                        options={option?.options?.map((item) => ({
                          value: item.id || item.value || item.name,
                          label: item.name || item.label,
                        }))}
                        onChange={(selectedOption) =>
                          handleSelection(option.title, selectedOption || [])
                        }
                        placeholder={option.title}
                        isMulti={true}
                        isClearable={true}
                        styles={customSelectStyles}
                        menuPortalTarget={selectMenuPortalTarget}
                        menuPosition="fixed"
                      />
                    </div>
                  );
                }

                return (
                  <DropDownFilter
                    key={i}
                    title={option.title}
                    options={option.options}
                    onSelect={(selectedItem) =>
                      handleSelection(option.title, selectedItem)
                    }
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function DropDownFilter({ title, options, onSelect }) {
  const [show, setShow] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [dateValue, setDateValue] = useState(null);
  const { lang } = useContext(Language);

  const handleDropdownClick = () => {
    setShow(!show);
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
    onSelect(item);
    setShow(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedItem(null);
    setDateValue(null);
    onSelect(null);
    setShow(false);
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!event.target.closest(".position-relative")) {
        setShow(false);
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  // ── Dedicated Modern Date Picker for Date Filter ──
  if (title === lang?.date || title === "Date") {
    const isSelected = Boolean(dateValue);
    return (
      <ModernDatePicker
        value={dateValue}
        onChange={(newDate) => {
          setDateValue(newDate);
          onSelect(newDate);
        }}
        format="MM/DD/YYYY"
        placeholder={`${lang?.select || "Select"} ${title}`}
        renderTrigger={({ displayValue, onClick }) => {
          return (
            <button
              type="button"
              onClick={onClick}
              className={`search-pill-dropdown-btn ${isSelected ? "selected" : ""}`}
            >
              <span>
                {isSelected
                  ? (dateValue && moment(dateValue).isValid() ? moment(dateValue).format("MM/DD/YYYY") : displayValue)
                  : `${lang?.select || "Select"} ${title}`}
              </span>
              {isSelected ? (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDateValue(null);
                    onSelect(null);
                  }}
                  className="search-pill-clear-btn"
                  title={lang?.clear || "Clear filter"}
                >
                  <LuX size={11} />
                </span>
              ) : (
                <IoIosArrowDown size={13} />
              )}
            </button>
          );
        }}
      />
    );
  }

  // ── Dedicated Modern Month Picker for Month Filter ──
  if (title === lang?.month || title === "Month") {
    const isSelected = Boolean(dateValue);
    return (
      <ModernMonthPicker
        value={dateValue}
        onChange={(selectedObj) => {
          if (selectedObj) {
            setDateValue(selectedObj.moment);
            onSelect(selectedObj.moment);
          } else {
            setDateValue(null);
            onSelect(null);
          }
        }}
        placeholder={`${lang?.select || "Select"} ${title}`}
        renderTrigger={({ displayValue, onClick }) => {
          return (
            <button
              type="button"
              onClick={onClick}
              className={`search-pill-dropdown-btn ${isSelected ? "selected" : ""}`}
            >
              <span>
                {isSelected
                  ? (dateValue && moment(dateValue).isValid() ? moment(dateValue).format("MMMM YYYY") : displayValue)
                  : `${lang?.select || "Select"} ${title}`}
              </span>
              {isSelected ? (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDateValue(null);
                    onSelect(null);
                  }}
                  className="search-pill-clear-btn"
                  title={lang?.clear || "Clear filter"}
                >
                  <LuX size={11} />
                </span>
              ) : (
                <IoIosArrowDown size={13} />
              )}
            </button>
          );
        }}
      />
    );
  }

  const isSelected = Boolean(selectedItem);

  return (
    <div className="position-relative">
      <button
        type="button"
        onClick={handleDropdownClick}
        className={`search-pill-dropdown-btn ${isSelected ? "selected" : ""}`}
      >
        <span>
          {selectedItem
            ? typeof selectedItem === "string"
              ? selectedItem
              : selectedItem.name
            : title}
        </span>
        {isSelected ? (
          <span
            role="button"
            tabIndex={0}
            onClick={handleClear}
            className="search-pill-clear-btn"
            title={lang?.clear || "Clear filter"}
          >
            <LuX size={11} />
          </span>
        ) : (
          <IoIosArrowDown size={13} />
        )}
      </button>

      {show && (
        <div className="search-dropdown-menu-card">
          <div className="search-dropdown-search-box">
            <IoIosSearch
              size={18}
              style={{
                position: "absolute",
                left: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#94A3B8",
              }}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={lang?.search || "Search..."}
              className="search-dropdown-search-input"
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {options && options.length > 0 ? (
              options
                .filter((val) =>
                  (typeof val === "string" ? val : val?.name || "")
                    .toLowerCase()
                    .includes(search.toLowerCase())
                )
                .map((val, index) => {
                  const optionName = typeof val === "string" ? val : val?.name || "";
                  const isOptionSelected =
                    selectedItem === val ||
                    (typeof selectedItem === "string" && selectedItem === optionName) ||
                    selectedItem?.name === optionName;

                  return (
                    <div
                      key={index}
                      onClick={() => handleItemClick(val)}
                      className={`search-dropdown-option-item ${isOptionSelected ? "selected" : ""}`}
                    >
                      {optionName}
                    </div>
                  );
                })
            ) : (
              <p style={{ fontSize: "13px", color: "#94A3B8", margin: "8px", textAlign: "center" }}>
                {lang?.no_results_found || "No options found"}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
